import { CustomEventsDatePicker } from 'modules/customEvents/components/CustomEventsDatePicker';
import { CustomEventsHeaderOverflowMenu } from 'modules/customEvents/components/CustomEventsHeaderOverflowMenu';
import {
  useCustomEventsQueryContext,
  useModalsContext,
  useSelectionContext,
} from 'modules/customEvents/contexts';
import { INGESTION_POLICIES, MergedCustomEvent } from 'modules/customEvents/types';
import { useSearchParamsContext } from 'modules/dataDictionary/contexts';
import { FieldDataType, FieldLockState } from 'modules/dataDictionary/types';
import { Action as BulkAction } from 'ui/bulkActionsBar';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { Add16, DatabaseRetain16 } from 'ui/icons';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { InputSearch } from 'ui/inputs';
import { Menu } from 'ui/menu';
import { IndexTabTrigger } from 'ui/tabsGroup';
import { FullWidth } from 'ui/utility/fullWidth';
import { FieldLockNotification } from '../FieldLockNotification';
import {
  Actions,
  Filters,
  SearchWrapper,
  StyledActionsBar,
  StyledLockNotification,
  StyledTabsList,
  Wrapper,
} from './CustomEventsActions.styled';

/**
 * Text
 */
export const CUSTOM_EVENTS_ACTIONS_TEXT = {
  actionsTestId: 'ingestion-policy-tabs',
  inputPlaceholder: 'Search events',
  newFieldButtonLabel: 'Define new event',
  newFieldButtonTestId: 'new-custom-event-button',
  searchInputTestId: 'search-event-fields',
  triggerLabels: {
    saved: 'Saved',
    discarded: 'Discarded',
    deactivated: 'Deactivated',
  },
};
const { triggerLabels } = CUSTOM_EVENTS_ACTIONS_TEXT;
const { Off, On } = FieldLockState;

type CustomEventsActionsProps = {
  filteredCustomEvents: MergedCustomEvent[];
};

export const CustomEventsActions = ({ filteredCustomEvents }: CustomEventsActionsProps) => {
  const { project } = useCustomEventsQueryContext();
  const { extendedProjectSettings } = project;
  const fieldLockState = extendedProjectSettings?.allowNewCustomEvents ? Off : On;
  const { showAddModal, showRetentionPeriodModal } = useModalsContext();
  const { selected } = useSelectionContext();
  const {
    endDate,
    journeyTriggers,
    preset,
    search,
    setEndDate,
    setJourneyTriggers,
    setPreset,
    setSearch,
    setStartDate,
    startDate,
  } = useSearchParamsContext();

  const bulkActions: BulkAction[] = [
    {
      icon: <DatabaseRetain16 />,
      label: 'Update retention policy',
      onClick: () => showRetentionPeriodModal(selected as string[]),
      sortIndex: 0,
    },
  ];

  const isEditMode = selected?.length > 0;

  const handleChange = (val: string) => {
    const newValues = journeyTriggers.includes(val) ? [] : [val];
    setJourneyTriggers(newValues);
  };

  return (
    <Wrapper data-mode={isEditMode ? 'edit' : 'view'}>
      <Actions>
        <StyledLockNotification>
          <FieldLockNotification
            fieldLockState={fieldLockState}
            fieldType={FieldDataType.CustomEvent}
          />
        </StyledLockNotification>

        <CustomEventsHeaderOverflowMenu
          allowNewCustomEvents={false}
          filteredCustomEvents={filteredCustomEvents}
        />

        <PrimaryButton
          size={BUTTON_SIZES.LARGE}
          data-test={CUSTOM_EVENTS_ACTIONS_TEXT.newFieldButtonTestId}
          onClick={showAddModal}
        >
          <Add16 />
          <span>{CUSTOM_EVENTS_ACTIONS_TEXT.newFieldButtonLabel}</span>
        </PrimaryButton>
      </Actions>
      {isEditMode ? (
        <FullWidth>
          <StyledActionsBar actions={bulkActions} numSelected={selected?.length} />
        </FullWidth>
      ) : (
        <Filters>
          <StyledTabsList data-test={CUSTOM_EVENTS_ACTIONS_TEXT.actionsTestId}>
            <IndexTabTrigger value={INGESTION_POLICIES.SAVED}>
              {triggerLabels.saved}
            </IndexTabTrigger>
            <IndexTabTrigger value={INGESTION_POLICIES.DISCARDED}>
              {triggerLabels.discarded}
            </IndexTabTrigger>
            <IndexTabTrigger value={INGESTION_POLICIES.DEACTIVATED}>
              {triggerLabels.deactivated}
            </IndexTabTrigger>
          </StyledTabsList>
          <CustomEventsDatePicker
            start={startDate}
            end={endDate}
            preset={preset}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setPreset={setPreset}
          />
          <Menu
            data-test="custom-event-filters"
            options={[
              {
                id: 'true',
                label: 'Triggers journey',
              },
            ]}
            defaultSelectedOptionIds={journeyTriggers}
            handleSelectionChange={handleChange}
            isSelectMenu
          >
            <FilterMenuTriggerButton selectedFilterCount={journeyTriggers.length} />
          </Menu>
          <SearchWrapper>
            <InputSearch
              data-test={CUSTOM_EVENTS_ACTIONS_TEXT.searchInputTestId}
              id="search-events"
              onChange={setSearch}
              placeholder={CUSTOM_EVENTS_ACTIONS_TEXT.inputPlaceholder}
              value={search}
            />
          </SearchWrapper>
        </Filters>
      )}
    </Wrapper>
  );
};
